import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import isEqual from 'lodash.isequal';
import { Response } from '@ui/src';
import {
  FeeType,
  FeeItem,
  FeeTransactionTypeEnum,
  FeeTypeEnum,
} from '../../../fees/types';
import { TariffFeeFormValues, TariffItemFee } from '../../types';
import { feesApi } from '../../../fees/store/services';
import { formatFeeList } from '../../helpers/format-fee-list';

export interface TariffsFeeFormState {
  formValues?: TariffFeeFormValues;
  feeTypes?: FeeType[];
  fee?: TariffItemFee;
  feeId?: string;
  isLoading: boolean;
}

const initialState: TariffsFeeFormState = {
  isLoading: false,
};

export const tariffsFeeFormSlice = createSlice({
  name: 'tariffsFeeForm',
  initialState,
  reducers: {
    clearFeeFormState: (state) => {
      state.fee = undefined;
      state.feeId = undefined;
      state.isLoading = false;
      state.formValues = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      feesApi.endpoints.getFeesList.matchFulfilled,
      (state, action: PayloadAction<Response<FeeItem>>) => {
        const { edges } = action.payload.data;

        const feesList: { type: string; subType?: string }[] = [];

        edges.forEach((item) => {
          const currentFee: {
            type: string;
            subType?: string;
          } = {
            type: item.node.feeType,
          };

          if (item.node.paymentType) {
            currentFee.subType = item.node.paymentType;
          } else if (item.node.regularFeeSubType) {
            currentFee.subType = item.node.regularFeeSubType;
          } else if (item.node.feeTransactionType === 'INTERNAL') {
            currentFee.type = item.node.feeTransactionType;
          }

          if (
            !feesList.find((fee) => {
              const feeItem: {
                type: string;
                subType?: string;
                list?: FeeItem[];
              } = {
                type: fee.type,
              };

              if (fee.subType) {
                feeItem.subType = fee.subType;
              }

              return isEqual(currentFee, feeItem);
            })
          ) {
            feesList.push(currentFee);
          }
        });

        const nextFees = feesList?.map((item) => {
          let result = { ...item };

          const fees = edges
            .filter(
              (fee) =>
                (fee.node.feeType === result.type &&
                  fee.node.feeTransactionType !==
                    FeeTransactionTypeEnum.INTERNAL) ||
                (result.type === FeeTypeEnum.INTERNAL &&
                  fee.node.feeTransactionType ===
                    FeeTransactionTypeEnum.INTERNAL),
            )
            .sort((a, b) => a?.node.name?.localeCompare(b?.node.name));

          if (fees.length) {
            fees.forEach((fee) => {
              result = formatFeeList({
                item: result as any,
                data: fee.node,
              }) as any;
            });
          }

          return result;
        });

        state.feeTypes = nextFees as unknown as FeeType[];
      },
    );
  },
});

export const { clearFeeFormState } = tariffsFeeFormSlice.actions;

export default tariffsFeeFormSlice.reducer;
